/* =====================================================================
 * xstore.js :: unico arquivo necessario no tema da vitrine (Loja A)
 *
 * Faz duas coisas:
 *   1. intercepta o checkout e manda o carrinho para o xstore-router
 *   2. (Fase 8a) cria o session_id e registra o funil no xstore-collect
 *
 * Instalacao:
 *   1. assets/xstore.js  (este arquivo)
 *   2. em layout/theme.liquid, antes de </body>:
 *        <script src="{{ 'xstore.js' | asset_url }}" defer></script>
 *
 * O tema nao guarda nenhum link, dominio ou variant ID de loja de
 * checkout. Ele so envia as SKUs do carrinho para o router.
 *
 * Fase 2c: a gaveta custom (sections/xstore-cart.liquid, modo Router) e
 * so interface sobre o carrinho nativo da vitrine. O botao "Zur Kasse"
 * dela leva `data-xstore-checkout`, que este script ja intercepta; os
 * atributos do carrinho nativo (`_desconto` do desconto progressivo)
 * viajam em `atributos` e o router aplica o codigo no cartCreate.
 *
 * Fase 2c.1: o checkout mede cada etapa (cart.js, router com os tempos
 * internos dele, total) e imprime `[xstore] checkout: ...` no console.
 * Nada do xstore-collect e aguardado em momento nenhum: enviar() dispara
 * e esquece.
 *
 * Fase 8c: a sessao passa a levar o `_fbp`, o identificador de navegador
 * da Meta. E o que faz o Purchase que o xstore-capi devolve encontrar
 * alguem quando a visita nao veio de um clique em anuncio (sem fbclid).
 * Lido do cookie do pixel, se houver; gerado no mesmo formato, se nao.
 *
 * ---------------------------------------------------------------------
 * CONSENTIMENTO (docs/atribuicao.md, secao 10)
 *
 * Nao ha banner aqui: o Horizon usa o banner nativo da Shopify. Este
 * script so PERGUNTA, pela Customer Privacy API, e se comporta conforme
 * a resposta:
 *
 *   com consentimento  -> session_id em COOKIE first-party de 30 dias,
 *                         click ids e _fbp capturados e enviados
 *   sem consentimento  -> session_id em sessionStorage (morre com a aba),
 *                         nenhum click id, nenhum _fbp, nenhum cookie
 *                         persistente
 *
 * Quando o visitante aceita depois (evento `visitorConsentCollected`), o
 * mesmo session_id migra para o cookie e os parametros da URL sao
 * reenviados — a sessao nao se perde, so deixa de ser anonima.
 * ===================================================================== */

(function () {
  'use strict';

  var VERSAO = '8c.1';

  var CONFIG = {
    endpoint: 'https://xstore-router-worker.lucasmramos0212.workers.dev',
    endpointColeta: 'https://xstore-collect-worker.lucasmramos0212.workers.dev/coletar',
    chaveSessao: 'xs_sessao',
    chaveUtm: 'xs_utm',
    chaveSid: 'xs_sid',
    utmTtlMs: 30 * 24 * 60 * 60 * 1000,
    sidTtlDias: 30,
    // Seletores dos botoes que devem levar ao checkout externo
    seletores: [
      '[name="checkout"]',
      '[data-xstore-checkout]',
      'form[action="/cart"] button[type="submit"]',
      'form[action^="/cart"] [name="checkout"]',
    ],
    mensagemErro: {
      de: 'Der Checkout ist momentan nicht verfügbar. Bitte versuchen Sie es in Kürze erneut.',
      nl: 'De kassa is momenteel niet beschikbaar. Probeer het zo meteen opnieuw.',
      en: 'Checkout is temporarily unavailable. Please try again shortly.',
    },
  };

  /** Quanto esperar a Customer Privacy API antes de seguir anonimo. */
  var ESPERA_CONSENTIMENTO_MS = 1500;

  var CAMPOS_UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var CAMPOS_CLICK = ['fbclid', 'ttclid', 'gclid', 'msclkid'];

  // ------------------------------------------------------------------
  // Consentimento :: Customer Privacy API da Shopify
  //
  // A API chega junto com o banner e pode nao estar pronta no primeiro
  // pageview. `window.Shopify.loadFeatures` a carrega sob demanda; ate
  // ela responder, tratamos como SEM consentimento (o padrao seguro).
  // ------------------------------------------------------------------
  function apiPrivacidade() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  function consentimentoOk() {
    return consentimentoConhecido() === true;
  }

  /**
   * true | false | null, e o `null` importa: "nao sei" NAO e "nao".
   *
   * Mandar `false` quando a API ainda nao respondeu faz o worker tratar a
   * requisicao como revogacao e apagar fbp, click ids e user_agent de uma
   * sessao que tinha consentimento. Com `null` ele mantem o que esta
   * gravado e nao grava identificador nenhum — que e o comportamento certo
   * para quem ainda nao sabe.
   */
  function consentimentoConhecido() {
    var api = apiPrivacidade();
    if (!api) return null;
    try {
      // analytics cobre a sessao; marketing cobre o click id e a CAPI.
      // Exigimos os dois: e o click id que sai daqui para a Meta.
      return api.analyticsProcessingAllowed() === true && api.marketingAllowed() === true;
    } catch {
      return null;
    }
  }

  function carregarApiPrivacidade(quandoPronta) {
    if (apiPrivacidade()) {
      quandoPronta();
      return;
    }
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    try {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (erro) {
          if (!erro) quandoPronta();
        }
      );
    } catch {
      /* tema sem a feature: seguimos anonimos */
    }
  }

  // ------------------------------------------------------------------
  // Armazenamento: cookie com consentimento, sessionStorage sem ele
  // ------------------------------------------------------------------
  function lerCookie(nome) {
    var partes = ('; ' + document.cookie).split('; ' + nome + '=');
    return partes.length === 2 ? decodeURIComponent(partes.pop().split(';').shift()) : null;
  }

  function gravarCookie(nome, valor, dias) {
    try {
      var expira = new Date(Date.now() + dias * 864e5).toUTCString();
      document.cookie =
        nome +
        '=' +
        encodeURIComponent(valor) +
        '; expires=' +
        expira +
        '; path=/; SameSite=Lax' +
        (window.location.protocol === 'https:' ? '; Secure' : '');
    } catch {
      /* cookie bloqueado: a sessao vive so nesta aba */
    }
  }

  function apagarCookie(nome) {
    try {
      document.cookie = nome + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    } catch {
      /* nada a fazer */
    }
  }

  // ------------------------------------------------------------------
  // _fbp :: o identificador de navegador que a Meta usa (Fase 8c)
  //
  // O pixel da Meta, quando existe na vitrine, grava este cookie. Aqui
  // ele e LIDO, nunca substituido: se o pixel ja escreveu, o Purchase
  // que o xstore-capi manda de volta casa com o que o navegador viu.
  //
  // Quando nao existe — vitrine sem pixel, que e o caso hoje — geramos no
  // mesmo formato, `fb.<subdominio>.<criado_em_ms>.<aleatorio>`. A Meta
  // aceita um fbp de origem propria; o que ela nao aceita e conversao sem
  // nenhum identificador. E se o pixel for instalado depois, ele encontra
  // o cookie e reaproveita o mesmo valor, entao os dois lados continuam
  // falando do mesmo navegador.
  //
  // Os 90 dias sao os do proprio pixel da Meta. Sem consentimento nao ha
  // cookie nenhum: isto e identificador de pessoa, igual ao click id.
  // ------------------------------------------------------------------
  function fbp() {
    if (!consentimentoOk()) return null;
    var atual = lerCookie('_fbp');
    if (atual && /^fb\.\d\.\d+\./.test(atual)) return atual;
    var novo = 'fb.1.' + Date.now() + '.' + Math.floor(Math.random() * 1e10);
    gravarCookie('_fbp', novo, 90);
    return novo;
  }

  function lerTemporario(chave) {
    try {
      return window.sessionStorage.getItem(chave);
    } catch {
      return null;
    }
  }

  function gravarTemporario(chave, valor) {
    try {
      window.sessionStorage.setItem(chave, valor);
    } catch {
      /* modo privado: a sessao vive so em memoria */
    }
  }

  // ------------------------------------------------------------------
  // session_id :: UUID v4, o elo que atravessa a fronteira de dominio
  // ------------------------------------------------------------------
  var sidEmMemoria = null;

  function novoUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // Fallback com getRandomValues; Math.random nunca, para nao gerar
    // colisao de session_id entre visitantes.
    var b = new Uint8Array(16);
    window.crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = [];
    for (var i = 0; i < 16; i++) h.push((b[i] + 0x100).toString(16).slice(1));
    return (
      h.slice(0, 4).join('') +
      '-' +
      h.slice(4, 6).join('') +
      '-' +
      h.slice(6, 8).join('') +
      '-' +
      h.slice(8, 10).join('') +
      '-' +
      h.slice(10, 16).join('')
    );
  }

  /**
   * O session_id de sempre, ou um novo. Com consentimento vale o cookie
   * (30 dias, atravessa visitas); sem ele, o sessionStorage (uma aba).
   */
  function sessionId() {
    var comConsentimento = consentimentoOk();
    var sid = lerCookie(CONFIG.chaveSid) || lerTemporario(CONFIG.chaveSid) || sidEmMemoria;

    if (!sid) sid = novoUuid();
    sidEmMemoria = sid;

    if (comConsentimento) gravarCookie(CONFIG.chaveSid, sid, CONFIG.sidTtlDias);
    else gravarTemporario(CONFIG.chaveSid, sid);

    return sid;
  }

  /** Consentimento revogado: o cookie sai, a sessao volta a ser da aba. */
  function rebaixarParaAnonimo() {
    apagarCookie(CONFIG.chaveSid);
    // Revogou: o identificador da Meta cai junto com o click id. Se o
    // pixel da Meta estiver instalado ele vai recriar o proprio cookie —
    // isso e com o banner da Shopify, nao com este script.
    apagarCookie('_fbp');
    if (sidEmMemoria) gravarTemporario(CONFIG.chaveSid, sidEmMemoria);
  }

  // ------------------------------------------------------------------
  // Parametros da chegada
  //
  // UTM continua indo para o pedido como atributo `_utm_*` (e o que o
  // xstore-orders le para preencher plataforma_ads). Click id NAO vai
  // para o carrinho: ele viaja pela sessao, que a CAPI da Fase 8c le.
  // ------------------------------------------------------------------
  function parametrosDaUrl() {
    var achado = { utm: {}, click: {}, temUtm: false, temClick: false };
    try {
      var params = new URLSearchParams(window.location.search);
      CAMPOS_UTM.forEach(function (c) {
        var v = params.get(c);
        if (v && v.trim()) {
          achado.utm[c] = v.trim().slice(0, 255);
          achado.temUtm = true;
        }
      });
      CAMPOS_CLICK.forEach(function (c) {
        var v = params.get(c);
        if (v && v.trim()) {
          achado.click[c] = v.trim().slice(0, 255);
          achado.temClick = true;
        }
      });
    } catch {
      /* URL estranha: seguimos sem parametros */
    }
    return achado;
  }

  function capturarUtm() {
    var p = parametrosDaUrl();
    if (!p.temUtm) return;

    var guardar = {};
    CAMPOS_UTM.forEach(function (c) {
      if (p.utm[c]) guardar['_' + c] = p.utm[c];
    });

    try {
      if (consentimentoOk()) {
        guardar.expira_em = Date.now() + CONFIG.utmTtlMs;
        window.localStorage.setItem(CONFIG.chaveUtm, JSON.stringify(guardar));
      } else {
        // Sem consentimento o UTM nao persiste entre visitas, mas ainda
        // acompanha ESTA visita ate o pedido — que e o caso comum.
        gravarTemporario(CONFIG.chaveUtm, JSON.stringify(guardar));
      }
    } catch {
      /* sem storage: o pedido sai sem UTM, e so */
    }
  }

  function lerUtm() {
    var bruto = null;
    try {
      bruto = window.localStorage.getItem(CONFIG.chaveUtm);
    } catch {
      bruto = null;
    }
    if (!bruto) bruto = lerTemporario(CONFIG.chaveUtm);
    if (!bruto) return {};

    try {
      var u = JSON.parse(bruto);
      if (!u) return {};
      if (u.expira_em && u.expira_em < Date.now()) {
        try {
          window.localStorage.removeItem(CONFIG.chaveUtm);
        } catch {
          /* ignora */
        }
        return {};
      }
      delete u.expira_em;
      return u;
    } catch {
      return {};
    }
  }

  // ------------------------------------------------------------------
  // Coleta :: envio para o xstore-collect
  //
  // `fetch` com `keepalive` sobrevive ao unload igual ao sendBeacon — e
  // que e o caso do begin_checkout, com a pagina indo embora em seguida —
  // e tem duas vantagens que o beacon nao tem:
  //
  //   1. `credentials: 'omit'`. O sendBeacon envia SEMPRE com credentials
  //      'include' (a spec nao deixa escolher), o que obriga o worker a
  //      devolver Access-Control-Allow-Credentials e proibe
  //      Allow-Origin: '*'. Isso quebrou a coleta na primeira validacao ao
  //      vivo, e o beacon nao reporta o erro ao script: a coleta sumiria
  //      em silencio. Depender de um cabecalho para nao sumir e fragil.
  //   2. Cookie nenhum e util para o coletor. Nao mandar e menos dado
  //      pessoal trafegando, que e a direcao certa sob GDPR.
  //
  // O beacon fica como ultimo recurso, para navegador sem fetch.
  // ------------------------------------------------------------------
  function dispositivo() {
    var ua = (navigator.userAgent || '').toLowerCase();
    if (/ipad|tablet|playbook|silk/.test(ua)) return 'tablet';
    if (/mobi|iphone|ipod|android|blackberry|iemobile|opera mini/.test(ua)) return 'mobile';
    return 'desktop';
  }

  function dadosDaSessao() {
    var p = parametrosDaUrl();
    var comConsentimento = consentimentoOk();

    var sessao = {
      origem: 'vitrine',
      primeiro_em: new Date().toISOString(),
      landing_url: window.location.href,
      referrer: document.referrer || null,
      dispositivo: dispositivo(),
      idioma: document.documentElement.lang || navigator.language || null,
    };

    CAMPOS_UTM.forEach(function (c) {
      if (p.utm[c]) sessao[c] = p.utm[c];
    });
    // O worker limpa de novo; aqui e para o click id nem sair do navegador.
    if (comConsentimento) {
      CAMPOS_CLICK.forEach(function (c) {
        if (p.click[c]) sessao[c] = p.click[c];
      });
      // A Meta casa a conversao por fbc (do clique) OU fbp (do
      // navegador). Sem fbclid, o fbp e o unico elo que sobra.
      var idMeta = fbp();
      if (idMeta) sessao.fbp = idMeta;
    }

    return sessao;
  }

  // NUNCA aguardada (2c.1): nenhum caminho do checkout ou do carrinho
  // espera esta requisicao. Quem chamar `enviar` nao recebe promise.
  function enviar(eventos) {
    if (!eventos || !eventos.length) return;

    var corpo = JSON.stringify({
      session_id: sessionId(),
      // true | false | null. O null e "a API nao respondeu ainda", e o
      // worker o trata como "nao mexe no que esta gravado".
      consentimento: consentimentoConhecido(),
      sessao: dadosDaSessao(),
      eventos: eventos,
    });

    try {
      if (window.fetch) {
        fetch(CONFIG.endpointColeta, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: corpo,
          keepalive: true,
          mode: 'cors',
          credentials: 'omit',
        }).catch(function () {
          /* coleta nunca atrapalha a loja */
        });
        return;
      }
      if (navigator.sendBeacon) {
        var blob = new Blob([corpo], { type: 'application/json' });
        navigator.sendBeacon(CONFIG.endpointColeta, blob);
      }
    } catch {
      /* idem */
    }
  }

  function rastrear(tipo, dados) {
    var evento = { tipo: tipo, ocorrido_em: new Date().toISOString(), url: window.location.href };
    if (dados) {
      if (dados.sku) evento.sku_canonica = dados.sku;
      if (dados.valor !== undefined && dados.valor !== null) evento.valor = dados.valor;
      if (dados.moeda) evento.moeda = dados.moeda;
      if (dados.payload) evento.payload = dados.payload;
    }
    enviar([evento]);
  }

  // ------------------------------------------------------------------
  // Funil da vitrine
  // ------------------------------------------------------------------
  function metaShopify() {
    try {
      return (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
    } catch {
      return {};
    }
  }

  function moedaDaLoja() {
    try {
      return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
    } catch {
      return null;
    }
  }

  /** A variante da URL (`?variant=`) ou a primeira do produto. */
  function varianteAtual(produto) {
    if (!produto || !produto.variants || !produto.variants.length) return null;
    var id = null;
    try {
      id = new URLSearchParams(window.location.search).get('variant');
    } catch {
      id = null;
    }
    if (id) {
      for (var i = 0; i < produto.variants.length; i++) {
        if (String(produto.variants[i].id) === String(id)) return produto.variants[i];
      }
    }
    return produto.variants[0];
  }

  function registrarPageview() {
    var meta = metaShopify();
    var tipoPagina = (meta.page && meta.page.pageType) || '';
    rastrear('pageview', { payload: { pagina: tipoPagina || 'outra' } });

    if (tipoPagina === 'product' && meta.product) {
      var v = varianteAtual(meta.product);
      rastrear('view_product', {
        sku: v && v.sku ? String(v.sku) : null,
        // meta.product traz preco em centavos.
        valor: v && typeof v.price === 'number' ? v.price / 100 : null,
        moeda: moedaDaLoja(),
        payload: { produto: meta.product.id ? String(meta.product.id) : null },
      });
    }
  }

  /**
   * add_to_cart: o tema adiciona pelo endpoint /cart/add, entao o gancho
   * e o proprio fetch. Envolver a funcao nativa vale para qualquer tema
   * (Horizon, Dawn, custom) sem depender de evento proprietario. Se algo
   * der errado aqui, a chamada original segue intacta.
   */
  function observarAddToCart() {
    if (!window.fetch) return;
    var original = window.fetch;

    window.fetch = function (entrada, _opcoes) {
      var resposta = original.apply(this, arguments);
      try {
        var url = typeof entrada === 'string' ? entrada : entrada && entrada.url;
        if (url && url.indexOf('/cart/add') !== -1) {
          resposta
            .then(function (r) {
              if (!r || !r.ok) return;
              return r
                .clone()
                .json()
                .then(function (item) {
                  if (!item) return;
                  // /cart/add.js devolve o item; com `items`, um lote.
                  var itens = item.items || [item];
                  itens.forEach(function (i) {
                    rastrear('add_to_cart', {
                      sku: i.sku ? String(i.sku) : null,
                      valor: typeof i.final_line_price === 'number' ? i.final_line_price / 100 : null,
                      moeda: moedaDaLoja(),
                      payload: { quantidade: i.quantity || 1 },
                    });
                  });
                });
            })
            .catch(function () {
              /* coleta nunca atrapalha a loja */
            });
        }
      } catch {
        /* idem */
      }
      return resposta;
    };
  }

  // ------------------------------------------------------------------
  // Sessao pegajosa do rodizio: mantem o cliente na mesma loja ate expirar
  // (nao confundir com o session_id da atribuicao)
  // ------------------------------------------------------------------
  function lerSessao() {
    try {
      var bruto = window.localStorage.getItem(CONFIG.chaveSessao);
      if (!bruto) return null;
      var s = JSON.parse(bruto);
      if (!s || !s.store_id || !s.expira_em || s.expira_em < Date.now()) {
        window.localStorage.removeItem(CONFIG.chaveSessao);
        return null;
      }
      return s;
    } catch {
      return null;
    }
  }

  function gravarSessao(sessao) {
    try {
      if (sessao && sessao.store_id) {
        window.localStorage.setItem(CONFIG.chaveSessao, JSON.stringify(sessao));
      }
    } catch {
      /* modo privado do navegador: seguimos sem sessao */
    }
  }

  function idioma() {
    var lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
    return CONFIG.mensagemErro[lang] ? lang : 'en';
  }

  // ------------------------------------------------------------------
  // Le o carrinho nativo da vitrine e extrai apenas SKU + quantidade
  // ------------------------------------------------------------------
  function lerCarrinho() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Falha ao ler /cart.js');
        return r.json();
      })
      .then(function (cart) {
        var itens = [];
        var semSku = [];

        (cart.items || []).forEach(function (item) {
          if (item.sku && String(item.sku).trim()) {
            itens.push({ sku: String(item.sku).trim(), qty: item.quantity });
          } else {
            semSku.push(item.title);
          }
        });

        if (semSku.length) {
          console.warn('[xstore] itens sem SKU ignorados:', semSku);
        }

        return { itens: itens, nota: cart.note || null, cart: cart };
      });
  }

  /**
   * Atributos do carrinho nativo que seguem para o router (Fase 2c). Hoje
   * e o `_desconto` que a gaveta grava; amanha, o que mais o tema puser
   * em /cart/update.js. So texto nao vazio: `AttributeInput.value` da
   * Storefront API e obrigatorio, e `_desconto: ''` (nivel desfeito)
   * nao pode custar o checkout. `_session_id` tem campo proprio.
   */
  function atributosDoCarrinho(cart) {
    var saida = {};
    var bruto = cart && cart.attributes;
    if (!bruto || typeof bruto !== 'object') return saida;
    Object.keys(bruto).forEach(function (chave) {
      var valor = bruto[chave];
      if (typeof valor !== 'string' || !valor.trim()) return;
      if (chave.toLowerCase() === '_session_id') return;
      saida[chave] = valor;
    });
    return saida;
  }

  // ------------------------------------------------------------------
  // Chama o router e redireciona
  // ------------------------------------------------------------------
  function agora() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  function irParaCheckout(botao) {
    var textoOriginal = botao ? botao.innerHTML : null;
    if (botao) {
      botao.setAttribute('disabled', 'disabled');
      botao.setAttribute('aria-busy', 'true');
    }

    var t0 = agora();
    var tCart = 0;

    return lerCarrinho()
      .then(function (dados) {
        tCart = agora();
        if (!dados.itens.length) {
          throw new Error('Carrinho sem itens com SKU');
        }

        // Ultimo evento da vitrine. O `keepalive` do fetch em enviar() e o
        // que faz a requisicao sobreviver ao redirect que vem logo depois;
        // o checkout_started sai do custom pixel, ja na loja de checkout.
        rastrear('begin_checkout', {
          valor: typeof dados.cart.total_price === 'number' ? dados.cart.total_price / 100 : null,
          moeda: (dados.cart && dados.cart.currency) || moedaDaLoja(),
          payload: {
            itens: dados.itens.length,
            skus: dados.itens
              .map(function (i) {
                return i.sku;
              })
              .join(',')
              .slice(0, 255),
          },
        });

        return fetch(CONFIG.endpoint + '/cart/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            itens: dados.itens,
            sessao: lerSessao(),
            nota: dados.nota,
            // O router poe isto em `_session_id` dos atributos do carrinho;
            // vira note_attributes do pedido e o xstore-orders o le.
            session_id: sessionId(),
            // Os do carrinho nativo entram primeiro: `_vitrine`, `_idioma` e
            // as UTMs sao deste script e ninguem os sobrescreve por
            // /cart/update.js.
            atributos: Object.assign(
              atributosDoCarrinho(dados.cart),
              {
                _vitrine: window.location.hostname,
                _idioma: document.documentElement.lang || '',
              },
              lerUtm()
            ),
          }),
        });
      })
      .then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok || !j.ok) throw new Error(j.erro || 'Falha no router');
          return j;
        });
      })
      .then(function (j) {
        var tFim = agora();
        try {
          var interno = j.tempos
            ? ' (rodizio ' + j.tempos.rodizio + ', preparo ' + j.tempos.preparo + ', shopify ' + j.tempos.shopify +
              ' ms; token ' + j.tempos.token + ')'
            : '';
          console.info(
            '[xstore] checkout: cart.js ' + Math.round(tCart - t0) + ' ms + router ' + Math.round(tFim - tCart) +
              ' ms' + interno + ' = ' + Math.round(tFim - t0) + ' ms'
          );
        } catch (e) {
          /* medir nunca atrapalha */
        }
        gravarSessao(j.sessao);
        window.location.href = j.checkout_url;
      })
      .catch(function (e) {
        console.error('[xstore]', e);
        if (botao) {
          botao.removeAttribute('disabled');
          botao.removeAttribute('aria-busy');
          if (textoOriginal !== null) botao.innerHTML = textoOriginal;
        }
        window.alert(CONFIG.mensagemErro[idioma()]);
      });
  }

  // ------------------------------------------------------------------
  // Intercepta os botoes de checkout, inclusive os injetados depois
  // (drawer de carrinho, seções recarregadas pelo Horizon)
  // ------------------------------------------------------------------
  function combina(alvo) {
    for (var i = 0; i < CONFIG.seletores.length; i++) {
      if (alvo.closest && alvo.closest(CONFIG.seletores[i])) {
        return alvo.closest(CONFIG.seletores[i]);
      }
    }
    return null;
  }

  document.addEventListener(
    'click',
    function (ev) {
      var botao = combina(ev.target);
      if (!botao) return;
      if (botao.hasAttribute('data-xstore-ignorar')) return;

      ev.preventDefault();
      ev.stopPropagation();
      irParaCheckout(botao);
    },
    true
  );

  // Bloqueia o submit direto do form de carrinho
  document.addEventListener(
    'submit',
    function (ev) {
      var form = ev.target;
      if (!form || !form.getAttribute) return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart') !== 0) return;
      if (form.hasAttribute('data-xstore-ignorar')) return;

      ev.preventDefault();
      irParaCheckout(form.querySelector('[name="checkout"]'));
    },
    true
  );

  // ------------------------------------------------------------------
  // Partida
  // ------------------------------------------------------------------
  var jaRegistrouPageview = false;

  function iniciar() {
    capturarUtm();
    if (!jaRegistrouPageview) {
      jaRegistrouPageview = true;
      registrarPageview();
    }
  }

  observarAddToCart();

  // ------------------------------------------------------------------
  // O primeiro pageview ESPERA a resposta da Customer Privacy API.
  //
  // Ate a Fase 8c isto era `carregarApiPrivacidade(iniciar); iniciar();` —
  // e o `iniciar()` sincrono ganhava a corrida sempre. Consequencia: todo
  // primeiro pageview saia com `consentimento: false`, e o worker, vendo
  // uma sessao que era `true` chegar como `false`, tratava como revogacao
  // e apagava fbp, click ids e user_agent. Como `jaRegistrouPageview` ja
  // estava marcado, o reenvio depois da API responder nunca acontecia.
  // Resultado: NENHUMA sessao da vitrine ficava com consentimento — e nao
  // era o visitante recusando.
  //
  // Agora quem chegar primeiro decide, e o timeout e a rede de seguranca
  // para o tema sem a feature (editor de tema, loja sem banner): ali a
  // sessao comeca anonima, que continua sendo o padrao seguro.
  // ------------------------------------------------------------------
  var jaDecidiu = false;
  function decidirEIniciar() {
    if (jaDecidiu) return;
    jaDecidiu = true;
    iniciar();
  }

  carregarApiPrivacidade(decidirEIniciar);
  setTimeout(decidirEIniciar, ESPERA_CONSENTIMENTO_MS);

  // O visitante respondeu ao banner: a sessao muda de regime na hora e o
  // toque e reenviado, agora com os click ids que ficaram de fora.
  document.addEventListener('visitorConsentCollected', function () {
    if (consentimentoOk()) {
      sessionId(); // migra o mesmo id do sessionStorage para o cookie
      capturarUtm();
      rastrear('outro', { payload: { evento: 'consentimento_concedido' } });
    } else {
      rebaixarParaAnonimo();
      rastrear('outro', { payload: { evento: 'consentimento_revogado' } });
    }
  });

  // API publica, caso voce queira chamar de um bloco do Horizon
  window.XStore = {
    versao: VERSAO,
    checkout: irParaCheckout,
    sessao: lerSessao,
    utm: lerUtm,
    sessionId: sessionId,
    consentimento: consentimentoOk,
    consentimentoConhecido: consentimentoConhecido,
    fbp: fbp,
    rastrear: rastrear,
    limparSessao: function () {
      window.localStorage.removeItem(CONFIG.chaveSessao);
    },
    lojaDaVez: function () {
      var s = lerSessao();
      var qs = s ? '?store_id=' + s.store_id + '&expira_em=' + s.expira_em : '';
      return fetch(CONFIG.endpoint + '/resolve' + qs).then(function (r) {
        return r.json();
      });
    },
  };
})();
